import { StyledCard } from 'components/Custom/card';
import { ScopeTable } from 'components/Custom/table';
import { PAGE_SIZE_RECAP } from 'pages/vat/report';
import * as React from 'react';
import { MenuSize, ViesStatus } from 'store/app/enums';
import { VatRunData } from 'store/vat/models';
import { DataContact } from 'store/vat/models/vatReportData';
import { getVIESProviderLink } from 'utils/get-provider-link';
import { formatNumberWithLocale, getDefaultLocale } from 'utils/numberFormat';
import {
  PreferenceSections,
  PreferenceActions,
  Preference,
} from 'utils/preferences';
import { StatusGetter } from 'utils/status-getter';
import { CardDescSpan } from 'components/Custom/spans';
import { Space } from 'antd';
import styled from 'styled-components';
import { ReactComponent as ExternalLinkIcon } from 'assets/images/icons/externalLinkIcon.svg';
import { VatCheckerUnavailableBanner } from 'pages/vies/temporarily-unavailable';
import { isVatCheckerDisabled } from 'utils/feature-flag-utils';
import i18n from 'i18next';

const RecapWrapper = styled.div`
  .recap-statement .ant-table-row .external-link-icon {
    visibility: hidden;
    fill: currentColor;
  }
  .recap-statement .ant-table-row:hover .external-link-icon {
    visibility: visible;
  }
`;

interface TableDataContact extends DataContact {
  key: number;
}
export const renderMaltaRecap = (
  contacts: DataContact[],
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any,
  vatRunData: VatRunData,
  _menuSize: MenuSize,
  _showSideMenu: boolean,
  showRotateHint = false
) => {
  const locale = getDefaultLocale();
  const pageSizeSaved = handlePreferences(
    PreferenceSections.RECAP,
    PreferenceActions.GET,
    {
      name: PAGE_SIZE_RECAP,
    }
  );

  const savePageSize = (pageSize: number) => {
    const preferenceSection = PreferenceSections.RECAP;
    handlePreferences(preferenceSection, PreferenceActions.SET, {
      name: PAGE_SIZE_RECAP,
      value: pageSize,
    });
  };
  const tableContacts: TableDataContact[] = [];
  contacts?.forEach((c, key) => tableContacts.push({ ...c, key }));
  const isEmptyRecap = tableContacts.length === 0;
  const columns = [
    {
      title: i18n.t('vat-report.triangular-trade'),
      render: (_, record: DataContact) => {
        return record.isTriangularTrade
          ? i18n.t('common.yes-uppercase')
          : i18n.t('common.no-uppercase');
      },
    },
    {
      title: i18n.t('vat-report.customer-name'),
      dataIndex: 'name',
      render: (_, record: DataContact) => {
        return (
          <a
            href={getVIESProviderLink(
              vatRunData.providerName?.toLowerCase(),
              vatRunData?.shortCode,
              'customer',
              record.providerContactId
            )}
            target="_blank"
            rel="noreferrer"
            //style={{ height: '32px', display: 'block' }}
          >
            {record.name}
            <ExternalLinkIcon
              className="external-link-icon"
              style={{
                marginLeft: '5px',
                width: '16px',
                height: '16px',
                verticalAlign: 'middle',
              }}
            />
          </a>
        );
      },
    },
    {
      title: i18n.t('common.country'),
      dataIndex: 'country',
    },
    {
      title: i18n.t('vat-report.vat-number'),
      dataIndex: 'taxNumber',
    },
    {
      title: i18n.t('vat-report.vat-verified'),
      dataIndex: 'viesStatus',
      render: (_, record: DataContact) => {
        return <StatusGetter status={ViesStatus[record.viesStatus]} noDraft />;
      },
    },
    {
      title: i18n.t('vat-report.goods-subtotal'),
      dataIndex: 'goodsSum',
      align: 'right',
      render: (_, record: DataContact) => {
        return formatNumberWithLocale(locale, record.goodsSum);
      },
    },
    {
      title: i18n.t('vat-report.services-subtotal'),
      dataIndex: 'servicesSum',
      align: 'right',
      render: (_, record: DataContact) => {
        return formatNumberWithLocale(locale, record.servicesSum);
      },
    },
  ];

  const renderHeaderComponent = () => {
    const totalGoods =
      contacts?.reduce((sum, item) => sum + item.goodsSum, 0) || 0;
    const totalServices =
      contacts?.reduce((sum, item) => sum + item.servicesSum, 0) || 0;
    return (
      <Space direction="horizontal" style={{ float: 'right' }}>
        <CardDescSpan
          style={{
            float: 'right',
            fontSize: 11,
          }}
        >
          {i18n.t('vat-report.totals-goods-subtotal')}{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {formatNumberWithLocale(locale, totalGoods)}
          </span>
          {'     '}
          {i18n.t('vat-report.services-subtotal')}{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {formatNumberWithLocale(locale, totalServices)}
          </span>
        </CardDescSpan>
      </Space>
    );
  };

  const renderMobileSummary = () => {
    const totalGoods =
      contacts?.reduce((sum, item) => sum + item.goodsSum, 0) || 0;
    const totalServices =
      contacts?.reduce((sum, item) => sum + item.servicesSum, 0) || 0;

    return (
      <div className="scope-pwa-recap-summary">
        <div className="scope-pwa-recap-summary__row">
          <span>{i18n.t('vat-report.totals-goods-subtotal')}</span>
          <strong>{formatNumberWithLocale(locale, totalGoods)}</strong>
        </div>
        <div className="scope-pwa-recap-summary__row">
          <span>{i18n.t('vat-report.services-subtotal')}</span>
          <strong>{formatNumberWithLocale(locale, totalServices)}</strong>
        </div>
      </div>
    );
  };

  // Card fills the report column beside the boxes panel.
  const getWidth = () => '100%';
  return (
    <RecapWrapper>
      {showRotateHint && (
        <div className="scope-pwa-recap-rotate-hint">
          Turn your phone sideways to view tables more easily.
        </div>
      )}
      {isVatCheckerDisabled() && (
        <VatCheckerUnavailableBanner width={getWidth()} />
      )}
      <StyledCard //@ts-ignore
        width={getWidth()}
        style={{
          overflowY: showRotateHint ? 'auto' : 'hidden',
          overflowX: showRotateHint ? 'hidden' : 'auto',
        }}
      >
        <div
          style={{
            padding: '10px',
            paddingLeft: '5px',
            paddingTop: showRotateHint ? '24px' : '10px',
            margin: 'auto',
          }}
        >
          {showRotateHint ? (
            <div className="scope-pwa-recap-cards">
              <div className="scope-pwa-recap-cards__summary">{renderMobileSummary()}</div>
              {isEmptyRecap ? (
                <div className="scope-pwa-recap-cards__empty">
                  No data found for this period
                </div>
              ) : (
                tableContacts.map((record) => (
                  <div className="scope-pwa-recap-card" key={record.key}>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.triangular-trade')}</span>
                      <strong>
                        {record.isTriangularTrade
                          ? i18n.t('common.yes-uppercase')
                          : i18n.t('common.no-uppercase')}
                      </strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.customer-name')}</span>
                      <strong>{record.name}</strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('common.country')}</span>
                      <strong>{record.country}</strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.vat-number')}</span>
                      <strong>{record.taxNumber}</strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.vat-verified')}</span>
                      <strong>
                        <StatusGetter status={ViesStatus[record.viesStatus]} noDraft />
                      </strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.goods-subtotal')}</span>
                      <strong>{formatNumberWithLocale(locale, record.goodsSum)}</strong>
                    </div>
                    <div className="scope-pwa-recap-card__row">
                      <span>{i18n.t('vat-report.services-subtotal')}</span>
                      <strong>{formatNumberWithLocale(locale, record.servicesSum)}</strong>
                    </div>
                  </div>
                ))
              )}
              <div className="scope-pwa-recap-table-landscape">
                <ScopeTable
                  className={`recap-statement${isEmptyRecap ? ' recap-statement--empty' : ''}`}
                  originalDataSource={tableContacts} //@ts-ignore
                  tableDataSource={tableContacts}
                  pagination={true}
                  rowsPerPage={pageSizeSaved ? pageSizeSaved : 10}
                  enableColumnsCustomization={false}
                  rowHeight={35}
                  columns={columns}
                  transactionType={'RECAP'}
                  reportId={vatRunData.openDataLogId}
                  titleComponent={renderHeaderComponent()}
                  showSizeChanger
                  getNewPageSize={savePageSize}
                  size={'small'}
                  scroll={{ x: isEmptyRecap ? '100%' : 'max-content' }}
                />
              </div>
            </div>
          ) : (
            <ScopeTable
              className={`recap-statement${isEmptyRecap ? ' recap-statement--empty' : ''}`}
              originalDataSource={tableContacts} //@ts-ignore
              tableDataSource={tableContacts}
              pagination={true}
              rowsPerPage={pageSizeSaved ? pageSizeSaved : 10}
              enableColumnsCustomization={false}
              rowHeight={35}
              columns={columns}
              transactionType={'RECAP'}
              reportId={vatRunData.openDataLogId}
              titleComponent={renderHeaderComponent()}
              showSizeChanger
              getNewPageSize={savePageSize}
              size={'small'}
              scroll={{ x: isEmptyRecap ? '100%' : 'max-content' }}
            />
          )}
        </div>
      </StyledCard>
    </RecapWrapper>
  );
};
